import * as React from 'react';
import { toast } from 'react-toastify';
import type { PriceListItem, PriceListTierList } from './models/AdminInterfaces';
import Pricelist from './Pricelist';
import AddPriceListTier from './AdminPriceListsAddTierModal';
import type { GenericList, ItemOptionList } from './models/JobSharedInterfaces';
import { CheckPermissions, GetQuery, PostQuery } from './Shared';
import { useNavigate } from 'react-router-dom';

const AdminPriceListEventData = () => {
    let navigate = useNavigate();
    const [priceListTiers, setPriceListTiers] = React.useState<PriceListTierList[]>([]);
    const [itemList, setItemList] = React.useState<ItemOptionList[]>([]);
    const [frequencyList, setFrequencyList] = React.useState<GenericList[]>([]);
    const [locationList, setLocationList] = React.useState<GenericList[]>([]);
    const [priceListItems, setPriceListItems] = React.useState<PriceListItem[]>([]);
    const [priceListTier, setPriceListTier] = React.useState({ priceListUniqueId: "", priceListTierId: -1, priceListTierType: -1 });
    const [systemCompanyId, setSystemCompanyId] = React.useState(3);
    const [loading, setLoading] = React.useState(false);
    const [pricelistLoading, setPricelistLoading] = React.useState(false);
    const [showAddTier, setShowAddTier] = React.useState(false);
    const [readOnly, setReadOnly] = React.useState(false);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});


    React.useEffect(() => {
        window.scrollTo(0, 0);

        getData();
    }, []);

    const getData = async () => {
        var hasPermission = await CheckPermissions("AdminPricelistsView");
        if (hasPermission) {
            getPriceListData();
        } else {
            navigate("/accessdenied");
        }
    }

    const getPriceListData = async () => {
        const apiUrl = "GetPriceListData?IsEws=false&IsPumpClean=false&IsEpa=false&IsFencing=false&IsEvent=true";
        var result = await GetQuery(apiUrl);

        if (result.error) {
            toast.error("Error getting data!");
        } else {
            setItemList(result.data.itemList);
            setFrequencyList(result.data.frequencyList);
            setLocationList(result.data.locationList);
            setPriceListTiers(result.data.priceListTier);
            setSystemCompanyId(result.data.systemCompanyId);
            setReadOnly(result.data.readOnly);
            setLoading(false);
        }
    }

    const getPriceList = async (id: number, typeId: number) => {
        if (id > 0) {
            const apiUrl = "GetPriceListEvent?Id=" + id + '&PriceListType=' + typeId;
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                var pricelist = result.data.priceList;
                let pricelistItem = addBlankRow(id, typeId * 1);
                pricelist.push(pricelistItem);
                setPriceListItems(pricelist);
                setPricelistLoading(false);
            }
        } else {
            setPriceListItems([]);
            setPricelistLoading(false);
        }
    }

    const addBlankRow = (id: number, typeId: number) => {
        var tierId = -1;
        var customerId = -1;
        if (typeId === 1) {
            tierId = id;
        } else {
            customerId = id;
        }

        let pricelistItem: PriceListItem = {
            id: 0,
            tierId: tierId,
            customerId: customerId,
            customerName: "",
            itemId: -1,
            itemTypeId: 0,
            hirePeriod: 0,
            frequencyId: -1,
            locationId: -1,
            metreId: -1,
            baseDays: 1,
            basePrice: 0,
            baseMetres: 0,
            useBtm: false,
            metreRate: 0,
            travelAmount: 0,
            baseAdditionalHireRate: 0,
            additionalHireRate: 0,
            extraHourRate: 0,
            overhireRequired: false,
            statusId: 1,
            isDirty: false
        };

        return pricelistItem;
    }

    const handlePriceListChange = (e: any) => {
        var uniqueId = e.target.value;
        var plId = uniqueId.substring(1) * 1;
        let typeId = 1;

        if (uniqueId != "") {
            let priceListOption = priceListTiers[e.target.selectedIndex - 1];
            if (priceListOption) {
                typeId = priceListOption.typeId;
            }
        }

        setPriceListTier({ priceListUniqueId: uniqueId, priceListTierId: plId, priceListTierType: typeId });

        if (uniqueId != "") {
            getPriceList(plId, typeId);
        }
    }

    //ADD TIER
    const addTier = () => {
        setShowAddTier(true);
    }

    const hideAddTier = () => {
        setShowAddTier(false);
    }

    const createNewBlankPriceListItem = (id: number, typeId: number) => {
        let pricelist: PriceListItem[] = [];
        let pricelistItem = addBlankRow(id, typeId);
        pricelist.push(pricelistItem);
        setPriceListItems(pricelist);
    }

    const saveAddTier = (customerTierId: number, name: string, typeId: number) => {
        //need to add the customer to the dropdown
        var uniqueType = typeId === 1 ? 'T' : 'C';
        var uniqueId = uniqueType + customerTierId;
        var newItem: PriceListTierList = {
            id: customerTierId,
            uniqueId: uniqueId,
            name: name,
            typeId: typeId
        };
        setPriceListTiers(prevList => [...prevList, newItem]);
        setPriceListTier(prevState => ({ ...prevState, priceListUniqueId: uniqueId, priceListTierId: customerTierId, priceListTierType: typeId }));
        createNewBlankPriceListItem(customerTierId, typeId);
        setShowAddTier(false);
    }
    //END ADD TIER

    const updatePricelist = (pricelist: PriceListItem[]) => {
        setPriceListItems(pricelist);
    }

    const validate = () => {
        let errorList: any = {};
        let formIsValid = true;

        if (priceListItems.length === 1) {
            errorList["pricelistitems"] = "Please enter at least one item!";
            setErrors(errorList);
            return true;
        }

        var rowNumber = 0;
        var errorString = "Please enter the following values: ";
        var error = false;

        for (var i = 0; i < priceListItems.length - 1; i++) {
            rowNumber += 1;
            var newErrorString = "";

            if (priceListItems[i].itemId < 1) {
                newErrorString += "Item, ";
                error = true;
            }

            if (!priceListItems[i].basePrice || priceListItems[i].basePrice <= 0) {
                newErrorString += "Price, ";
                error = true;
            }

            if (newErrorString != "") {
                errorString += "\n"
                errorString += "Row " + rowNumber + ": " + newErrorString;
                errorString = errorString.substring(0, errorString.length - 2);
            }

        }
        if (error) {
            formIsValid = false;
            errorList["pricelistitems"] = errorString;
        }
        setErrors(errorList);
        return formIsValid;
    }

    const save = (e: any) => {
        e.preventDefault();

        if (validate()) {
            var changed = priceListItems.filter(p => p.isDirty);

            if (changed.length === 0) {
                toast.info("No items have changed.");
            } else {
                savePricelist(changed);
            }
        } else {
            toast.error("Please fix validation errors before Saving")
        }
    }

    const savePricelist = async (changed: PriceListItem[]) => {
        var savePriceList = {
            priceListItems: changed
        };

        var jsonItem = JSON.stringify(savePriceList);

        const apiUrl = "SavePriceList";
        var result = await PostQuery(apiUrl, jsonItem);
        if (result.error) {
            toast.error("Error saving data!");
        } else {
            if (result.data.isError) {
                var errors = result.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                toast.success("Price List Saved Successfully");
                setPricelistLoading(true);

                getPriceList(priceListTier.priceListTierId, priceListTier.priceListTierType);
            }
        }
    }

    const addNewTier = <AddPriceListTier showModal={showAddTier} systemCompanyId={3} hideModal={hideAddTier} saveModal={saveAddTier} />

    const renderDetails = (
        <div>
            <div className="static-modal">
                {addNewTier}
            </div>
            <div className="input-group-parent">
                <label className="input-group" htmlFor="priceListTierId">
                    <span className="label">Tier/Customer</span>
                    <select className="select marginRight10" id="priceListTierId" name="priceListTierId" value={priceListTier.priceListUniqueId} onChange={(e) => handlePriceListChange(e)}>
                        <option hidden defaultValue=""></option>
                        {priceListTiers.map(tier =>
                            <option key={tier.uniqueId} value={tier.uniqueId}>{tier.name}</option>
                        )};
                    </select>
                    <button className={readOnly ? "hidden" : "defaultbutton defaultbutton__small"} type="button" onClick={addTier}>Add New</button>
                </label>
            </div>
            <hr></hr>
            <div className="makeitbold makeitred">NOTE 1: Travel Amount is the total amount that will be charged for travel (not a one-way rate and not per item)</div>
            <div className="makeitbold makeitred">NOTE 2: Additional Hire Rate is not mandatory</div>
            <div className="makeitbold makeitred">NOTE 3: If event items go into overhire they will use original overhire price NOT this pricelist rate!</div>
            <div className={priceListTier.priceListTierId > 0 && !pricelistLoading ? "" : "hidden"}>
                {<Pricelist systemCompanyId={systemCompanyId} id={priceListTier.priceListTierId} priceListTierTypeId={priceListTier.priceListTierType} pricelist={priceListItems} itemList={itemList} customerOptions={[]} frequencyList={frequencyList} locationList={locationList} metresList={[]} isEpa={false} isPumpClean={false} isFencing={false} isFencingCustom={false} isEvent={true} isPriceOnly={false} update={updatePricelist} />}
                {errors["pricelistitems"] ?
                    (errors["pricelistitems"]).split("\n").map((item: any, key: any) => {
                        return <span className="label errors" key={key}>{item}<br /></span>
                    })
                    : ""}
            </div>
            <div className={pricelistLoading ? "" : "hidden"}><p><em>Loading...</em></p></div>
            <div className="marginBottom10">
                <button className={readOnly ? "hidden" : "defaultbutton defaultbutton__large"} type="button" onClick={save} disabled={pricelistLoading || priceListTier.priceListTierId <= 0 ? true : false}>Save</button>
            </div>
        </div>
    );

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            <h1>Price List Events</h1>
            {contents}
        </div>
    );


}

export default AdminPriceListEventData;